import React, { useCallback, useMemo, useEffect, useRef, useState, Fragment } from "react";
import { useSearchParams } from "react-router-dom";
import { useFormik } from "formik";
import * as yup from "yup";

import COMMON from "common";
import ERRORS from "common/errors";
import api from "services/api";
import sanitizeObject from "common/sanitize-object";
import placeholderFormat from "common/placeholder-format";
import queryParamsEntries from "common/query-params-entries";
import serveRequestErrors from "common/serve-request-errors";
import { NOTIFICATION_REFERENCE_PARAM } from "common/notification-routes";
import { CALENDAR_FORMAT, formatDate, formatDatePattern, isDateBeforeToday } from "common/calendar";
import { formatTimeValue } from "common/time";
import AppInput from "components/app-input";
import AppStatus from "components/app-status";
import AppButton from "components/app-button";
import AppEmptyState from "components/app-empty-state";
import AppSearchInput from "components/app-search-input";
import AppSelectInput from "components/app-select-input";
import AppCalendarInput from "components/app-calendar-input";
import AppTable, { AppTableCell, AppTableEditIcon } from "components/app-table";
import AppOvertimeModal, { OVERTIME_MODAL_CONTEXT } from "components/pages/overtime-management/app-overtime-modal";
import viewIcon from "assets/images/view-icon.svg";
import filterIcon from "assets/images/components/app-multi-select-input/filter-icon.svg";

const formatSearchDate = (value) => (value ? formatDatePattern(new Date(value).toDateString(), CALENDAR_FORMAT.CALENDAR_FORMAT) : "");

const PageOvertimeTeamHistory = (props) => {
	const overtimeModalRef = useRef();
	const searchInputRef = useRef();
	const [searchParams, setSearchParams] = useSearchParams();
	const memoSetSearchParams = useRef(setSearchParams);
	
	//prettier-ignore
	const paramsRef = useRef({
		page: parseInt(searchParams.get("page")) || 1,
		sort: searchParams.get("sort") || "",
		employeeName: searchParams.get("employeeName") || "",
		status: searchParams.get("status") || "",
		startDate: searchParams.get("startDate") || "",
		endDate: searchParams.get("endDate") || "",
	});

	const notificationRefId = useRef(searchParams.get(NOTIFICATION_REFERENCE_PARAM) || "");

	const [data, setData] = useState({ page: 0, size: 10, total: 0, offset: 0, totalElements: 0, prev: false, next: false, items: [] });
	const [loading, setLoading] = useState(true);
	const cancelRequest = useMemo(() => props.onHandleCancelRequest, [props.onHandleCancelRequest]);
	const calendarDisplayFormat = useMemo(() => CALENDAR_FORMAT.DATE_FORMAT + " " + CALENDAR_FORMAT.MONTH_FORMAT + " " + CALENDAR_FORMAT.YEAR_FORMAT, []);
	const hasActiveFilters = useMemo(() => {
		const params = queryParamsEntries(searchParams);

		return !!(params.employeeName || params.status || params.startDate || params.endDate);
	}, [searchParams]);

	const isEmptyState = useMemo(() => !loading && data.items.length === 0 && !hasActiveFilters, [loading, data.items, hasActiveFilters]);
	const isEmptySearchResult = useMemo(() => !loading && data.items.length === 0 && hasActiveFilters, [loading, data.items, hasActiveFilters]);
	const initialValues = useMemo(() => ({ employeeName: "", status: "", startDate: "", endDate: "" }), []);
	
	//prettier-ignore
	const formik = useFormik({
		initialValues,
		validationSchema: yup.object({
			startDate: yup.string().test("startDate", ERRORS.START_DATE, function (value) {
				if (!value || !this.parent.endDate) return true;

				return isDateBeforeToday(value, this.parent.endDate);
			}),
			endDate: yup.string().test("endDate", ERRORS.END_DATE, function (value) {
				if (!value || !this.parent.startDate) return true;

				return isDateBeforeToday(this.parent.startDate, value);
			}),
		}),
		onSubmit: (values) => {
			onHandleSubmitSearch(values);
		},
	});

	const setValues = useMemo(() => formik.setValues, [formik]);

	const advanceSearchValues = useMemo(() => {
		const params = queryParamsEntries(searchParams);
		const status = COMMON.DROPDOWNS.OVERTIME_STATUS.find((o) => o.value === params.status);

		return {
			employeeName: params.employeeName,
			status: status?.label || params.status,
			startDate: params.startDate ? "From " + formatDatePattern(params.startDate, calendarDisplayFormat) : "",
			endDate: params.endDate ? "To " + formatDatePattern(params.endDate, calendarDisplayFormat) : "",
		};
	}, [searchParams, calendarDisplayFormat]);

	const onHandleGetList = useCallback(async () => {
		let response = null;

		setLoading(true);
		setData((prev) => ({ ...prev, items: [] }));

		try {
			const payload = { ...paramsRef.current, size: 10 };

			memoSetSearchParams.current(sanitizeObject(payload), { replace: true });

			payload.page = paramsRef.current.page - 1;

			response = await api.get.overtime.managerSearch(sanitizeObject(payload));
		} catch (error) {
			serveRequestErrors(error);
		}

		if (response) {
			setData((prev) => ({
				...prev,
				page: paramsRef.current.page,
				prev: !response.first,
				next: !response.last,
				items: response.content,
				total: response.totalPages,
				totalElements: response.totalElements,
				offset: response?.pageable?.offset || 0,
				status: response?.pageable,
			}));
		}

		setLoading(false);
	}, []);

	//prettier-ignore
	const onHandlePagination = useCallback((event) => {
		const control = event.currentTarget?.getAttribute("data-ctrl");

		if (control) {
			if (control === "prev") {
				if (paramsRef.current.page <= 1) return;
				paramsRef.current.page -= 1;
			} else {
				if (paramsRef.current.page >= data.total) return;
				paramsRef.current.page += 1;
			}
		} else {
			paramsRef.current.page = event.target.value;
		}

		onHandleGetList();
	}, [onHandleGetList, data.total]);

	const onHandleShowAdvanceSearch = (event) => {
		searchInputRef.current?.onHandleShowAdvanceSearch(event);
	};

	const onHandleSubmitSearch = (values) => {
		searchInputRef.current.onhandleCloseAdvanceSearch();

		const criteria = { ...values, startDate: formatSearchDate(values.startDate), endDate: formatSearchDate(values.endDate) };

		formik.setValues(criteria);

		paramsRef.current = { ...paramsRef.current, page: 1, ...criteria };

		onHandleGetList();
	};

	const onHandleRemoveField = (field) => {
		formik.setFieldValue(field, "");

		paramsRef.current = { ...paramsRef.current, page: 1, [field]: "" };

		onHandleGetList();
	};

	const onHandleResetSearch = () => {
		formik.setValues(formik.initialValues);

		paramsRef.current = { ...paramsRef.current, page: 1, ...formik.initialValues };

		onHandleGetList();
	};

	const onHandleViews = useCallback((id) => {
		overtimeModalRef.current.onHandleShow(id);
	}, []);

	const OvertimeDateCell = useCallback(({ row }) => <AppTableCell center value={formatDate(row.original.overtimeDate)} />, []);

	const StartTimeCell = useCallback(({ row }) => <AppTableCell center value={formatTimeValue(row.original.startTime) || "-"} />, []);

	const EndTimeCell = useCallback(({ row }) => <AppTableCell center value={formatTimeValue(row.original.endTime) || "-"} />, []);

	const SubmitDateCell = useCallback(({ row }) => <AppTableCell center value={formatDate(row.original.createdDate)} />, []);

	const HoursCell = useCallback(({ row }) => <AppTableCell center value={row.original.overtimeHours ?? "-"} />, []);

	//prettier-ignore
	const ReplacementLeaveCell = useCallback(({ row }) => <AppTableCell center value={row.original.replacementLeave ?? "-"} />, []);

	const StatusCell = useCallback(({ row }) => <AppStatus status={row.original.status} description={row.original.statusDescription} />, []);

	//prettier-ignore
	const MenuViewCell = useCallback(({ row }) => {
		return <AppTableEditIcon icon={viewIcon} onClick={() => onHandleViews(row.original.id)} />;
	}, [onHandleViews]);

	//prettier-ignore
	const columns = useMemo(() => [
		{
			Header: "Employee Name",
			accessor: "employeeName",
			disableSortBy: true,
		},
		{
			Header: "Overtime Date",
			accessor: "overtimeDate",
			disableSortBy: true,
			Cell: OvertimeDateCell,
		},
		{
			Header: "Start Time",
			accessor: "startTime",
			disableSortBy: true,
			Cell: StartTimeCell,
		},
		{
			Header: "End Time",
			accessor: "endTime",
			disableSortBy: true,
			Cell: EndTimeCell,
		},
		{
			Header: "Hours",
			accessor: "overtimeHours",
			disableSortBy: true,
			Cell: HoursCell,
		},
		{
			Header: "Replacement Leave",
			accessor: "replacementLeave",
			disableSortBy: true,
			Cell: ReplacementLeaveCell,
		},
		{
			Header: "Submit Date",
			accessor: "createdDate",
			disableSortBy: true,
			Cell: SubmitDateCell,
		},
		{
			Header: "Status",
			accessor: "status",
			disableSortBy: true,
			Cell: StatusCell,
		},
		{
			Header: "",
			accessor: "*",
			disableSortBy: true,
			Cell: MenuViewCell,
		},
	], [OvertimeDateCell, StartTimeCell, EndTimeCell, HoursCell, ReplacementLeaveCell, SubmitDateCell, StatusCell, MenuViewCell]);

	//prettier-ignore
	const headerButtons = useMemo(() => [{ label: "Filter", icon: filterIcon, outline: true, onClick: onHandleShowAdvanceSearch }], []);

	useEffect(() => {
		onHandleGetList();
	}, [onHandleGetList]);

	useEffect(() => {
		const { page, sort, ...res } = paramsRef.current;

		setValues((prev) => ({ ...prev, ...res }));
	}, [setValues]);

	// opens the details modal for notification
	useEffect(() => {
		const referenceId = searchParams.get(NOTIFICATION_REFERENCE_PARAM) || notificationRefId.current;

		if (!referenceId) return;

		notificationRefId.current = "";

		overtimeModalRef.current?.onHandleShow(referenceId);

		memoSetSearchParams.current(sanitizeObject({ ...paramsRef.current, size: 10 }), { replace: true });
	}, [searchParams]);

	useEffect(() => {
		return () => {
			cancelRequest(COMMON.ENDPOINT_PATH.OVERTIME.MANAGER_SEARCH);
		};
	}, [cancelRequest]);

	return (
		<div className="page-overtime-team-history">
			<div className="overtime-team-history">
				{isEmptyState && <AppEmptyState title="No records found" description="You don’t have any records yet" disabledButton />}

				{!isEmptyState && (
					<Fragment>
						<AppSearchInput ref={searchInputRef} multiValues={advanceSearchValues} onRemoveField={onHandleRemoveField} buttons={headerButtons}>
							<form className="app-advance-search-form" onSubmit={formik.handleSubmit}>
								<div className="advance-form">
									<div className="advance-form__inputs">
										<AppInput type="text" name="employeeName" label="Employee Name" placeholder="Enter employee name" value={formik.values.employeeName} onChange={formik.handleChange} />

										<AppSelectInput searchable={false} name="status" label="Status" placeholder="Select a status" options={COMMON.DROPDOWNS.OVERTIME_STATUS} value={formik.values.status} onChange={formik.handleChange} />

										<AppCalendarInput name="startDate" label="Overtime Date From" placeholder={placeholderFormat} displayFormat={calendarDisplayFormat} value={formik.values.startDate} error={formik.errors.startDate} touched={formik.touched.startDate} onChange={formik.setFieldValue} />

										<AppCalendarInput name="endDate" label="Overtime Date To" placeholder={placeholderFormat} displayFormat={calendarDisplayFormat} value={formik.values.endDate} error={formik.errors.endDate} touched={formik.touched.endDate} onChange={formik.setFieldValue} />
									</div>

									<div className="advance-form__button-container">
										<AppButton type="button" label="Clear" outline onClick={onHandleResetSearch} />
										<AppButton type="submit" label="Search" />
									</div>
								</div>
							</form>
						</AppSearchInput>

						{hasActiveFilters && !loading && (
							<div className="overtime-team-history__results">
								<p className="overtime-team-history__text">{data.totalElements} results found</p>
							</div>
						)}

						{isEmptySearchResult && <AppEmptyState title="No records found" description="Try adjusting your search or filters." disabledButton />}

						{!isEmptySearchResult && <AppTable columns={columns} pages={data} onHandlePagination={onHandlePagination} />}
					</Fragment>
				)}
			</div>

			<AppOvertimeModal ref={overtimeModalRef} context={OVERTIME_MODAL_CONTEXT.MANAGER} />
		</div>
	);
};

export default PageOvertimeTeamHistory;
